import React from 'react';
import {
  isGoogleAuthPause, isGoogleCalendarGone, isSwitchedOffGoogle, type GoogleHealth,
} from './calendarHealth';

export { NotificationSettings } from './NotificationSettings';
export { CalendarSettings } from './CalendarSettings';
export { GoogleAuthNotice } from './GoogleAuthNotice';
export {
  isSwitchedOffGoogle, isGoogleAuthPause, isGoogleCalendarGone, googleAuthNotice, googleRowError,
} from './calendarHealth';
export { ProfileSection } from './ProfileSection';
export { TimetableSection, timetableValue } from './TimetableSection';

/** Anything carrying a provider's sync health, as the API sends it. */
type SyncHealth = { sync_status?: string | null };

/**
 * The gear's brick "!" (Almanac D14): something is broken, and only that.
 * A connected calendar whose sync stopped (`paused`) or failed (`error`) is the
 * one thing that qualifies. Never Plus, never a missing preference — the mark
 * reads `sync_status` and nothing else, so it cannot turn into a nag.
 */
export function calendarsNeedAttention(calendars: SyncHealth[]): boolean {
  return calendars.some((c) => c.sync_status === 'paused' || c.sync_status === 'error');
}

/**
 * How many connected calendars need the person, across every provider whose
 * web payload carries sync health: Google (`CalendarSerializer.sync_status`)
 * and subscribed ICS feeds. The gear, the Settings page and the sheet all
 * count through here, so they cannot disagree. Microsoft is absent because
 * its web endpoint (`microsoft_calendar_list`) sends neither `sync_status` nor
 * `last_error`; there is nothing to read.
 */
export function countCalendarsNeedingAttention(
  google: GoogleHealth[] | null | undefined,
  subscribed: SyncHealth[] | null | undefined,
): number {
  // A switched-off Google calendar never counts (see isSwitchedOffGoogle).
  const enabledGoogle = (google ?? []).filter((c) => !isSwitchedOffGoogle(c));
  // A lost Google grant is ONE problem however many calendars it paused;
  // per-calendar problems (a permission pause; a calendar Google says is gone,
  // stored `disabled` + `not_found`) and ICS feeds count one each.
  const accountLost = enabledGoogle.some(isGoogleAuthPause) ? 1 : 0;
  const perRow = enabledGoogle.filter(
    (c) => !isGoogleAuthPause(c) && (calendarsNeedAttention([c]) || isGoogleCalendarGone(c)),
  ).length;
  const feeds = (subscribed ?? []).filter((c) => calendarsNeedAttention([c])).length;
  return perRow + feeds + accountLost;
}

export interface SettingsRowProps {
  /** Names the setting. */
  label: string;
  /** Its current value; omitted when nothing real backs one. */
  value?: string;
  /** Something here needs the person: a brick "!" mark beside the value. */
  need?: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** Id of the region the row opens, for aria-controls. */
  controls: string;
  testId?: string;
}

/**
 * One line of the Settings contents page (Almanac S6): the setting's name, its
 * current value, and a chevron. Pressing it opens the section in place below.
 * Written without JSX because this module is a plain `.ts` index.
 */
export const SettingsRow: React.FC<SettingsRowProps> = ({
  label, value, need = false, expanded, onToggle, controls, testId,
}) => React.createElement(
  'button',
  {
    type: 'button',
    className: 'alm-set-row',
    'aria-expanded': expanded,
    'aria-controls': controls,
    onClick: onToggle,
    'data-testid': testId,
  },
  React.createElement('span', { className: 'alm-what' }, label),
  React.createElement(
    'span',
    { className: `alm-v${need ? ' alm-need' : ''}` },
    need ? React.createElement('span', { className: 'alm-need-mark', 'aria-hidden': true }, '!') : null,
    value ?? '',
  ),
  React.createElement('span', { className: 'alm-chev', 'aria-hidden': true }, '›'),
);
